// theme.ts
import "vuetify/styles";
import { createVuetify } from "vuetify";
import { VCalendar } from "vuetify/labs/VCalendar";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";

/**
 * Declare colorSpecials ONCE and reuse everywhere.
 * Requested mapping:
 *  - colorSpecial1: Warm Orange (primary CTA/action)
 *  - colorSpecial2: Teal (growth/positive)
 *  - colorSpecial3: Deep Blue (trust/anchor)
 *  - colorSpecial4: Dark Gray
 *  - colorSpecial5: Slate Gray
 */
const brandSpecials = {
  colorSpecial1: "#FF2E63", // warm orange
  colorSpecial2: "#08D9D6", // teal
  colorSpecial3: "#00ADB5", // deep blue
  colorSpecial4: "#252A34", // dark gray
  colorSpecial5: "#393E46", // slate gray
};

const darkTheme = {
  dark: true,
  colors: {
    primary: "#EAEAEA",
    secondary: "#222831",
    accent: brandSpecials.colorSpecial1, // reuse
    error: "#FF5252",
    info: "#2196F3",
    success: "#4CAF50",
    warning: "#FFC107",

    buttonNavbar: "#393E46",
    buttonNavbarHover: "#222831",
    buttonNavbarActive: "#393E46",
    buttonNavbarActiveHover: "#222831",

    // CTAs use colorSpecial1 (orange)
    button: brandSpecials.colorSpecial1,
    buttonHover: brandSpecials.colorSpecial1,
    buttonActive: brandSpecials.colorSpecial1,
    buttonActiveHover: brandSpecials.colorSpecial1,

    buttonSecondary: "#bdbdbd",
    buttonSecondaryHover: "#bdbdbd",
    buttonSecondaryActive: "#bdbdbd",
    buttonSecondaryActiveHover: "#bdbdbd",

    background: "#252A34",
    background1: "#252A34",
    background2: "#08D9D6",
    background3: "#222831",

    textPrimary: "#EEEEEE",
    textSecondary: "#222831",
    textTertiary: "#EEEEEE",
    textQuaternary: "#707070",

    card: "#393E46",

    // inject all specials exactly once
    ...brandSpecials,

    chatBackground: "#393E46",
    chatRoomBackgroundActive: "#252A34",
    chatRoomBackgroundHover: "#252A34",
    chatSenderBackground: "#EEEEEE",
    authBackground: "#393E46",
    dialogBackground: "#393E46",
    settingsButtonBackground: "#FFFFFF",
    settingsButtonBackgroundActive: "#EAEAEA",
    borderSettingsCard: "#EAEAEA",
    disabled: "#707070",
    blogpostPreviewBackground: "#252A34",
  },
};

const lightTheme = {
  dark: false,
  colors: {
    primary: "#222831",
    secondary: "#393E46",

    // accent follows the CTA color
    accent: brandSpecials.colorSpecial1,

    buttonNavbar: "#393E46",
    buttonNavbarHover: "#222831",
    buttonNavbarActive: "#393E46",
    buttonNavbarActiveHover: "#222831",

    // CTAs use colorSpecial1 (orange)
    button: brandSpecials.colorSpecial1,
    buttonHover: brandSpecials.colorSpecial1,
    buttonActive: brandSpecials.colorSpecial1,
    buttonActiveHover: brandSpecials.colorSpecial1,

    buttonSecondary: "#bdbdbd",
    buttonSecondaryHover: "#bdbdbd",
    buttonSecondaryActive: "#bdbdbd",
    buttonSecondaryActiveHover: "#bdbdbd",

    background: "#EAEAEA",
    background1: "#EEEEEE",
    background2: "#08D9D6",
    background3: "#FFFFFF",

    textPrimary: "#222831",
    textSecondary: "#FFFFFF",
    textTertiary: "#EEEEEE",
    textQuaternary: "#707070",

    card: "#FFFFFF",

    // inject all specials exactly once
    ...brandSpecials,

    chatBackground: "#FFFFFF",
    chatRoomBackgroundActive: "#EAEAEA",
    chatRoomBackgroundHover: "#EAEAEA",
    chatSenderBackground: "#EEEEEE",
    authBackground: "#FFFFFF",
    dialogBackground: "#FFFFFF",
    settingsButtonBackground: "#FFFFFF",
    settingsButtonBackgroundActive: "#EAEAEA",
    borderSettingsCard: "#EAEAEA",
    disabled: "#707070",
    blogpostPreviewBackground: "#EAEAEA",
  },
};

export default createVuetify({
  components: { VCalendar, ...components },
  directives,
  theme: {
    defaultTheme: "lightTheme",
    themes: { darkTheme, lightTheme },
  },
});
